import { useCallback, useEffect, useState } from 'react'
import {
  initialAssistantOpen,
  isPlatformPath,
  readAssistantOpen,
  readFullMenuWithAssistant,
  writeAssistantOpen,
  writeFullMenuWithAssistant,
} from './assistantLayout'

export const DESKTOP_MIN_WIDTH = 1024

function readIsDesktop() {
  if (typeof window === 'undefined') return true
  return window.innerWidth >= DESKTOP_MIN_WIDTH
}

/**
 * The desktop AI panel's state (see assistantLayout.ts): open or hidden
 * (remembered), and whether the full menu stays beside it (this session).
 * Below 1024 px the AI is the drawer, which Shell opens on demand.
 */
export function useAssistantLayout({ pathname, enabled }: { pathname: string; enabled: boolean }) {
  const [isDesktop, setIsDesktop] = useState(readIsDesktop)
  const [open, setOpenState] = useState(() => initialAssistantOpen(pathname))
  const [fullMenuWithAssistant, setFullMenuState] = useState(readFullMenuWithAssistant)

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // The first platform page opens the AI, once: it is stored as the user's
  // choice, so every later visit follows whatever they pick.
  useEffect(() => {
    if (!enabled || !isDesktop || !isPlatformPath(pathname)) return
    if (readAssistantOpen() !== null) return
    writeAssistantOpen(true)
    setOpenState(true)
  }, [enabled, isDesktop, pathname])

  const setOpen = useCallback((next: boolean) => {
    writeAssistantOpen(next)
    setOpenState(next)
  }, [])

  const setFullMenuWithAssistant = useCallback((full: boolean) => {
    writeFullMenuWithAssistant(full)
    setFullMenuState(full)
  }, [])

  return {
    isDesktop,
    /** Desktop: the AI panel is on screen. */
    open,
    setOpen,
    /** Desktop, AI open: the full menu instead of the icon column. */
    fullMenuWithAssistant,
    setFullMenuWithAssistant,
  }
}
